import { ArdoqId, GroupType, ReferenceDirection } from '@ardoq/api-types';
import { RuleNotificationParams } from '@ardoq/query-builder';
import { SelectOption } from '@ardoq/select';

export type PerspectivesGroupsWorkspace = {
  name: string;
  id: ArdoqId;
};

export type PerspectivesGroupsReferenceTypes = {
  name: string;
  id: number;
};
export type PerspectivesGroupsFields = {
  name: string;
  id: ArdoqId;
};

export type PerspectivesGroupsTags = {
  name: string;
  id: ArdoqId;
};

export type PerspectivesGroupsComponentTypes = {
  name: string;
  id: ArdoqId;
};

export type PerspectivesGroupsOptions = {
  appliedGroupingRules: PerspectiveGroupingRule[];
  workspaces: PerspectivesGroupsWorkspace[];
  referenceTypesPerWorkspace: Record<
    ArdoqId,
    PerspectivesGroupsReferenceTypes[]
  >;
  componentTypesPerWorkspace: Record<
    ArdoqId,
    PerspectivesGroupsComponentTypes[]
  >;
  fieldsPerWorkspace: Record<ArdoqId, PerspectivesGroupsFields[]>;
  tagsPerWorkspace: Record<ArdoqId, PerspectivesGroupsTags[]>;
  globalReferenceTypeNames: string[];
  globalComponentTypeNames: string[];
};

export type PerspectiveGroupingRule =
  | WorkspaceGroupingRule
  | ComponentTypeGroupingRule
  | ReferenceGroupingRule
  | ReferenceTypeNameGroupingRule
  | ParentGroupingRule
  | ParentAllGroupingRule
  | TagGroupingRule
  | FieldGroupingRule
  | ChildTypeGroupingRule;

export type WorkspaceGroupingRule = {
  id: string;
  type: GroupType.WORKSPACE;
  sublabel?: string;
};

export type ComponentTypeGroupingRule = {
  id: string;
  type: GroupType.COMPONENT;
};

export type TagGroupingRule = {
  id: string;
  type: GroupType.TAG;
  targetId?: string; // tag
  workspaceId: string;
};

export type FieldGroupingRule = {
  id: string;
  type: GroupType.FIELD;
  targetId: string; // fieldId
  workspaceId: string;
};

export type ChildTypeGroupingRule = {
  id: string;
  type: GroupType.CHILD;
  targetId: string; // componentTypeId
  workspaceId: string;
};

export type ReferenceGroupingRule = {
  id: string;
  type: GroupType.REFERENCE;
  targetId: string; // referenceTypeId cast to string
  workspaceId: string;
  direction: ReferenceDirection;
  sublabel: string | undefined;
};

export type ReferenceTypeNameGroupingRule = {
  id: string;
  type: GroupType.REFERENCE_TYPE_NAME;
  targetId: string; // referenceTypeName
  direction: ReferenceDirection;
  sublabel: string | undefined;
};

export type ParentAllGroupingRule = {
  id: string;
  type: GroupType.PARENT_ALL;
  sublabel: string | undefined;
};

export type ParentGroupingRule = {
  id: string;
  type: GroupType.PARENT;
  targetId: string; // componentTypeId
  workspaceId: string;
  sublabel: string | undefined;
};

export type GetRuleNotificationParams = (
  rule: PerspectiveGroupingRule
) => RuleNotificationParams | undefined;

const referenceDirectionValues = new Set(Object.values(ReferenceDirection));

export const isPerspectiveGroupingRule = (
  candidate: Record<string, any>
): candidate is PerspectiveGroupingRule => {
  const { type, id, workspaceId, direction, targetId } = candidate;
  switch (type) {
    case GroupType.WORKSPACE:
    case GroupType.COMPONENT:
    case GroupType.PARENT_ALL:
      return id !== undefined;

    case GroupType.TAG:
    case GroupType.FIELD:
    case GroupType.CHILD:
    case GroupType.PARENT:
      return id !== undefined && workspaceId !== undefined;

    case GroupType.REFERENCE:
      return (
        id !== undefined &&
        workspaceId !== undefined &&
        referenceDirectionValues.has(direction)
      );

    case GroupType.REFERENCE_TYPE_NAME:
      return (
        id !== undefined &&
        referenceDirectionValues.has(direction) &&
        targetId !== undefined
      );

    default:
      return false;
  }
};

export type GroupingRuleSelectOptions = {
  workspaceSelectOption?: SelectOption<string>;
  ruleTargetSelectOption?: SelectOption<string>;
};

export type GetInvalidGroupingSelectOptions = (
  rule: PerspectiveGroupingRule
) => GroupingRuleSelectOptions;

export type GetGroupDisabledProps = (rule: PerspectiveGroupingRule) => {
  isDisabled: boolean;
};
